import { TextInput } from '@mgmresorts/mgm-ui';
import React, { useMemo } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input';

import { useContent } from '../../../../hooks/useContent';
import { BindInput } from '../../../../hooks/useInput';

type PhoneNumberInputProps = BindInput & {
  className?: string;
  disabled?: boolean;
  invalidFields?: string[];
  name: string;
  testId?: string;
  countryIso?: string; // Add country ISO prop
};

export function PhoneNumberInput({ name, invalidFields, disabled, countryIso, ...props }: PhoneNumberInputProps) {
  const { getContentByPath } = useContent();

  const error = useMemo(
    (): string | undefined =>
      invalidFields && invalidFields.includes(name) ? getContentByPath('shared.fields.phoneNumber.error') : undefined,
    [getContentByPath, invalidFields, name],
  );

  // Auto-detect country code from phone number value if countryIso is not provided
  const effectiveCountryIso = useMemo(() => {
    if (countryIso) {
      return countryIso;
    }

    if (props.value && typeof props.value === 'string' && props.value.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(props.value);
        return parsed?.country || 'US';
      } catch (error) {
        return 'US';
      }
    }

    return 'US';
  }, [countryIso, props.value]);

  return (
    <div data-testid='phone-number-input-wrapper'>
      <TextInput
        variant='phone-number'
        id='phoneNumber'
        label='Phone Number'
        value={props.value}
        countryIso={effectiveCountryIso}
        error={!!error}
        hint={error}
        onPhoneNumberChange={(value: string) => {
          props.onChange({
            target: {
              value,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        }}
        onBlur={props.onBlur}
        disabled={disabled}
        testId={props.testId || 'phone-number-input'}
        data-private='true'
        autoComplete='tel-national'
      />
    </div>
  );
}
